import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useClients } from '../hooks/useClients'
import { useAllowedDomains } from '../hooks/useAllowedDomains'

interface Exclusion {
  pattern: string
  note: string | null
  created_at: string
  created_by: string | null
}

export function SettingsPage() {
  return (
    <div className="mx-auto max-w-3xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Console-wide configuration.
        </p>
      </div>

      <AnalyticsExclusionsCard />
    </div>
  )
}

function AnalyticsExclusionsCard() {
  const [items, setItems] = useState<Exclusion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pattern, setPattern] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  // Suggestions: existing clients (full email) + allowed domains (as @domain).
  const { clients } = useClients()
  const { domains } = useAllowedDomains()
  const existingPatterns = useMemo(() => new Set(items.map(i => i.pattern)), [items])
  const suggestions = useMemo(() => {
    const out: { value: string; label: string }[] = []
    for (const c of clients) {
      if (!existingPatterns.has(c.email)) out.push({ value: c.email, label: c.email + (c.name ? ` — ${c.name}` : '') })
    }
    for (const d of domains) {
      const v = `@${d.domain}`
      if (!existingPatterns.has(v)) out.push({ value: v, label: `${v}${d.note ? ` — ${d.note}` : ''}` })
    }
    return out
  }, [clients, domains, existingPatterns])

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/settings/analytics-exclusions')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json() as { exclusions: Exclusion[] }
      setItems(data.exclusions || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { reload() }, [])

  async function add() {
    const p = pattern.trim()
    if (!p) return
    setAdding(true)
    setAddError(null)
    try {
      const res = await fetch('/api/admin/settings/analytics-exclusions', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pattern: p }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      const newItem = await res.json() as Exclusion
      // Optimistic insert so suggestions filter immediately, without waiting
      // for the reload round-trip.
      setItems(prev =>
        [...prev.filter(x => x.pattern !== newItem.pattern), newItem]
          .sort((a, b) => a.pattern.localeCompare(b.pattern)),
      )
      setPattern('')
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err))
    } finally {
      setAdding(false)
    }
  }

  async function remove(p: string) {
    if (!confirm(`Remove ${p} from analytics exclusions?`)) return
    setDeleting(p)
    try {
      const res = await fetch(`/api/admin/settings/analytics-exclusions/${encodeURIComponent(p)}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      setItems(prev => prev.filter(x => x.pattern !== p))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Analytics exclusions</CardTitle>
        <p className="text-xs text-muted-foreground">
          Emails and domain patterns excluded from operator-facing analytics signals
          (Hydrated last-visit + Growing status). Detailed event logs are unaffected.
          <br />
          Use <code className="font-mono">@example.com</code> to match an entire
          domain, or <code className="font-mono">me@example.com</code> for a single
          address. Clients with role <code className="font-mono">admin</code> are
          always excluded automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* List */}
        <div className="space-y-1">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No exclusions configured.</p>
          ) : (
            <ul className="divide-y border rounded">
              {items.map(item => (
                <li key={item.pattern} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <code className="font-mono text-foreground">{item.pattern}</code>
                  {item.note && <span className="text-xs text-muted-foreground flex-1 truncate">— {item.note}</span>}
                  {!item.note && <span className="flex-1" />}
                  <span className="text-xs text-muted-foreground/70 whitespace-nowrap">
                    {item.created_by ?? '—'} · {new Date(item.created_at).toLocaleDateString()}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={deleting === item.pattern}
                    onClick={() => remove(item.pattern)}
                    title="Remove"
                  >
                    {deleting === item.pattern
                      ? <Loader2 className="size-3.5 animate-spin" />
                      : <Trash2 className="size-3.5" />}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add form */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex gap-2">
            <Input
              list="analytics-exclusion-suggestions"
              placeholder="Pick a client/domain or type me@example.com / @example.com"
              value={pattern}
              onChange={e => setPattern(e.target.value)}
              disabled={adding}
              onKeyDown={e => { if (e.key === 'Enter') add() }}
              className="font-mono flex-1"
            />
            <datalist id="analytics-exclusion-suggestions">
              {suggestions.map(s => (
                <option key={s.value} value={s.value} label={s.label} />
              ))}
            </datalist>
            <Button onClick={add} disabled={adding || !pattern.trim()}>
              {adding ? <Loader2 className="size-4 animate-spin mr-1" /> : <Plus className="size-4 mr-1" />}
              Add
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/70">
            {suggestions.length} suggestion(s) available ({clients.length} client(s), {domains.length} allowed domain(s)). Free text is accepted too.
          </p>
          {addError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">{addError}</AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
